/**
 * PublicQuestionnairePage.Views
 * -----------------------------
 * Small presentational building blocks for the public questionnaire flow.
 *
 * Why split these out?
 * - The main page component contains a lot of state + side-effect logic (resume, autosave, submit).
 * - These views isolate the three “full screen” states so the main file stays readable:
 *   1) Loading: block interaction while initialization is in-flight
 *   2) Error: invalid/expired link or missing resource (user cannot proceed)
 *   3) Submitted: terminal acknowledgement after successful submission
 *
 * UX behavior:
 * - Each view uses full-viewport centering (minHeight="100vh") for a consistent experience.
 * - Styling is intentionally minimal and relies on MUI components for accessibility defaults.
 *
 * Notes:
 * - These views are intentionally “dumb”: no data fetching, no token logic, no localStorage.
 * - Translation function `t` is passed only where needed (Submitted view).
 */

import { Box, Text, Paper, Alert } from '../../ui';
import { MarkLoader } from '../brand/MarkLoader';
import { CheckCircle as CheckCircleIcon } from '../../ui/icons';
import type { TFunction } from 'i18next';

/**
 * Full-page loading indicator.
 * Used during initialization/resume when questionnaire definition and/or submission state
 * is still being determined.
 */
export function PublicQuestionnaireFullPageLoading() {
  return (
    <Box styles={{ backgroundColor: 'background.default', alignItems: 'center', display: 'flex', justifyContent: 'center', minHeight: '100vh' }}>
      <MarkLoader size="page" />
    </Box>
  );
}

/**
 * Full-page blocking error view.
 * This is used for “cannot proceed” scenarios (e.g., invalid token link or questionnaire not found).
 *
 * Props:
 * - error: the primary error message (already localized by the caller)
 * - checkLinkText: secondary guidance text (e.g., “check your link”)
 */
export function PublicQuestionnaireFullPageError(props: { error: string; checkLinkText: string }) {
  return (
    <Box styles={{ backgroundColor: 'background.default', alignItems: 'center', display: 'flex', justifyContent: 'center', minHeight: '100vh' }}>
      <Paper styles={{ p: 4, maxWidth: 400, textAlign: 'center' }}>
        <Alert severity="error" styles={{ mb: 2 }}>
          {props.error}
        </Alert>
        <Text level="body2" tone="text.secondary">
          {props.checkLinkText}
        </Text>
      </Paper>
    </Box>
  );
}

/**
 * Terminal submission acknowledgement.
 *
 * Behavior:
 * - Always thanks the user and confirms the questionnaire is submitted.
 * - If submission created a new user record (public flow), we show an extra informational banner.
 *
 * Note:
 * - This view intentionally does not offer “edit” or “back” actions because the submission is final.
 */
export function PublicQuestionnaireSubmittedView(props: { isNewUser: boolean; t: TFunction }) {
  return (
    <Box styles={{ backgroundColor: 'background.default', alignItems: 'center', display: 'flex', justifyContent: 'center', minHeight: '100vh' }}>
      <Paper styles={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
        {/* Visual confirmation of completion */}
        <CheckCircleIcon styles={{ fontSize: 64, color: 'success.main', mb: 2 }} />

        <Text level="h5" gutterBottom>
          {props.t('publicQuestionnairePage.thankYou')}
        </Text>

        <Text level="body1" tone="text.secondary" styles={{ mb: 2 }}>
          {props.t('publicQuestionnairePage.submitted')}
        </Text>

        {/* Only shown when the backend indicates a new account was created during submission */}
        {props.isNewUser && (
          <Alert severity="info" styles={{ mt: 2 }}>
            {props.t('publicQuestionnairePage.newAccountCreated')}
          </Alert>
        )}
      </Paper>
    </Box>
  );
}
